import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GearSix } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

// Shop-wide nesting rules used by "AI layout" (saved under settings/nesting)
export const NestingSettingsPopover = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || vals) return;
    api
      .get("/settings/nesting")
      .then(({ data }) => {
        setFields(data.fields || []);
        setVals(data.settings || {});
      })
      .catch(() => toast.error("Could not load nesting settings"));
  }, [open, vals]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/nesting", vals);
      setVals(data.settings);
      onChange?.(data.settings);
      toast.success("Nesting settings saved");
      setOpen(false);
    } catch {
      toast.error("Could not save nesting settings");
    }
    setSaving(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="layout-nesting-settings"
          title="Nesting settings"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-700 text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-100"
        >
          <GearSix size={15} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 border-zinc-800 bg-zinc-950 p-4 text-zinc-200">
        <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-amber-200">Nesting settings</div>
        {!vals ? (
          <div className="text-xs text-zinc-500">Loading…</div>
        ) : (
          <div className="space-y-2.5">
            {fields.map((f) =>
              f.type === "bool" ? (
                <label key={f.key} className="flex cursor-pointer items-center gap-2.5 text-xs">
                  <input
                    type="checkbox"
                    data-testid={`nesting-${f.key}`}
                    checked={!!vals[f.key]}
                    onChange={(e) => setVals({ ...vals, [f.key]: e.target.checked })}
                    className="h-4 w-4 accent-amber-400"
                  />
                  <span>{f.label}</span>
                </label>
              ) : (
                <label key={f.key} className="flex items-center justify-between gap-3 text-xs">
                  <span>{f.label}</span>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    data-testid={`nesting-${f.key}`}
                    value={vals[f.key] ?? ""}
                    onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                    className="font-mono-data h-8 w-20 rounded border border-zinc-700 bg-zinc-900 px-2 text-right text-xs outline-none focus:border-amber-400"
                  />
                </label>
              ),
            )}
            <button
              type="button"
              data-testid="nesting-save"
              disabled={saving}
              onClick={save}
              className="mt-2 h-9 w-full rounded-lg bg-amber-400 text-[10px] font-bold uppercase tracking-widest text-zinc-950 transition-colors duration-150 hover:bg-amber-300 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
